import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { FiX } from 'react-icons/fi';
import { printingBrandingAPI } from '../../services/api';

const PrintingBrandingForm = ({ record, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    party_name: '',
    item: '',
    quantity: '',
    branding_type: '',
    side: 'Single Side',
    logo_name: '',
    size: '',
    rate_of_priority: '',
    remarks: ''
  });
  const [loading, setLoading] = useState(false);
  const [itemSuggestions, setItemSuggestions] = useState([]);
  const [showItemSuggestions, setShowItemSuggestions] = useState(false);
  const [itemSearchLoading, setItemSearchLoading] = useState(false);
  const itemInputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        itemInputRef.current &&
        !itemInputRef.current.contains(event.target) &&
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target)
      ) {
        setShowItemSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (record) {
      setFormData({
        date: record.date ? record.date.split('T')[0] : new Date().toISOString().split('T')[0],
        party_name: record.party_name || '',
        item: record.item || '',
        quantity: record.quantity || '',
        branding_type: record.branding_type || '',
        side: record.side || 'Single Side',
        logo_name: record.logo_name || '',
        size: record.size || '',
        rate_of_priority: record.rate_of_priority || '',
        remarks: record.remarks || ''
      });
    }
  }, [record]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Fetch item suggestions
  const fetchItemSuggestions = async (query) => {
    if (!query || query.length < 1) {
      setItemSuggestions([]);
      return;
    }
    try {
      setItemSearchLoading(true);
      const response = await printingBrandingAPI.getItemSuggestions(query);
      setItemSuggestions(response.data.data || []);
    } catch (error) {
      console.error('Error fetching item suggestions:', error);
    } finally {
      setItemSearchLoading(false);
    }
  };

  // Handle item input change
  const handleItemChange = (e) => {
    const value = e.target.value;
    setFormData({ ...formData, item: value });
    setShowItemSuggestions(true);
    fetchItemSuggestions(value);
  };

  // Handle item suggestion selection
  const handleItemSelect = (item) => {
    setFormData({ ...formData, item: item });
    setShowItemSuggestions(false);
    setItemSuggestions([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.party_name) {
      toast.error('Please enter party name');
      return;
    }
    if (!formData.item) {
      toast.error('Please enter item');
      return;
    }
    if (!formData.branding_type) {
      toast.error('Please select branding type');
      return;
    }

    try {
      setLoading(true);
      if (record) {
        await printingBrandingAPI.update(record.id, formData);
        toast.success('Record updated successfully');
      } else {
        await printingBrandingAPI.create(formData);
        toast.success('Record created successfully');
      }
      onSuccess();
    } catch (error) {
      console.error('Error saving record:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold text-gray-800">
            {record ? 'Edit Printing / Branding' : 'Add Printing / Branding'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <FiX size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6" style={{ height: '70vh', overflowY: 'auto' }}>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              {/* Party Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Party Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="party_name"
                  value={formData.party_name}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter party name"
                  required
                />
              </div>

              {/* Item with Auto-suggestion */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Item <span className="text-red-500">*</span>
                </label>
                <input
                  ref={itemInputRef}
                  type="text"
                  name="item"
                  value={formData.item}
                  onChange={handleItemChange}
                  onFocus={() => {
                    if (formData.item) {
                      setShowItemSuggestions(true);
                      fetchItemSuggestions(formData.item);
                    }
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Type to search or enter custom item"
                  autoComplete="off"
                  required
                />
                {/* Suggestions Dropdown */}
                {showItemSuggestions && (itemSuggestions.length > 0 || itemSearchLoading) && (
                  <div
                    ref={suggestionsRef}
                    className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto"
                  >
                    {itemSearchLoading ? (
                      <div className="px-4 py-2 text-gray-500 text-sm">Searching...</div>
                    ) : (
                      itemSuggestions.map((item, index) => (
                        <div
                          key={index}
                          className="px-4 py-2 hover:bg-blue-50 cursor-pointer text-sm border-b last:border-b-0"
                          onClick={() => handleItemSelect(item)}
                        >
                          {item}
                        </div>
                      ))
                    )}
                  </div>
                )}
                {formData.item && !itemSearchLoading && itemSuggestions.length === 0 && showItemSuggestions && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                    <div className="px-4 py-2 text-gray-500 text-sm">
                      No matches found. Custom item "{formData.item}" will be used.
                    </div>
                  </div>
                )}
              </div>

              {/* Quantity */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quantity
                </label>
                <input
                  type="number"
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleChange}
                  min="0"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter quantity"
                />
              </div>

              {/* Type of Branding */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type of Branding <span className="text-red-500">*</span>
                </label>
                <select
                  name="branding_type"
                  value={formData.branding_type}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select Type</option>
                  <option value="DTF">DTF</option>
                  <option value="Screen Printing">Screen Printing</option>
                  <option value="Sublimation">Sublimation</option>
                  <option value="UV">UV</option>
                </select>
              </div>

              {/* Side */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Side <span className="text-red-500">*</span>
                </label>
                <select
                  name="side"
                  value={formData.side}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="Single Side">Single Side</option>
                  <option value="Both Side">Both Side</option>
                </select>
              </div>

              {/* Logo Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Logo Name
                </label>
                <input
                  type="text"
                  name="logo_name"
                  value={formData.logo_name}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter logo name"
                />
              </div>

              {/* Size */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Size
                </label>
                <input
                  type="text"
                  name="size"
                  value={formData.size}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter size"
                />
              </div>

              {/* Rate of Priority */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rate of Printing
                </label>
                <input
                  type="number"
                  name="rate_of_priority"
                  value={formData.rate_of_priority}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter rate"
                />
              </div>
            </div>

            {/* Remarks */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Remarks
              </label>
              <textarea
                name="remarks"
                value={formData.remarks}
                onChange={handleChange}
                rows="3"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter any remarks"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Saving...' : record ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PrintingBrandingForm;
